import { useState } from 'react';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { mockMeetings, mockClients, mockProjects } from '@/mocks/data';
import { Link } from 'react-router-dom';
import type { Meeting, Client, Project } from '@/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Upload, Clock, ChevronRight, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';

function MeetingsContent() {
  const [viewMode, setViewMode] = useState<'all' | 'grouped'>('grouped');
  const [selectedClient, setSelectedClient] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredMeetings = mockMeetings.filter((meeting) => {
    const matchesClient = selectedClient === 'all' || meeting.clientId === selectedClient;
    const matchesSearch =
      searchTerm === '' ||
      meeting.summary?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      meeting.participants.some((p) => p.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesClient && matchesSearch;
  });

  const groupedMeetings = mockClients.reduce(
    (acc, client) => {
      const clientMeetings = filteredMeetings.filter((m) => m.clientId === client.id);
      if (clientMeetings.length > 0) {
        acc[client.id] = {
          client,
          meetings: clientMeetings,
          projects: mockProjects.filter((p) => p.clientId === client.id),
        };
      }
      return acc;
    },
    {} as Record<string, { client: Client; meetings: Meeting[]; projects: Project[] }>
  );

  const upcomingMeetings = filteredMeetings
    .filter((m) => new Date(m.date) > new Date())
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays > 0 && diffDays <= 7) return `In ${diffDays} days`;
    if (diffDays < 0 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <Breadcrumb items={[{ label: 'Meetings & Notes' }]} />
          <h1 className="text-3xl font-bold text-foreground">Meetings & Notes</h1>
          <p className="text-muted-foreground mt-1">Manage your client meetings and project discussions</p>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/meetings/upload">
              <Upload className="w-4 h-4 mr-2" />
              Upload Notes
            </Link>
          </Button>
          <Button asChild>
            <Link to="/meetings/new">
              <Plus className="w-4 h-4 mr-2" />
              New Meeting
            </Link>
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="border border-border">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground mb-1">Total Meetings</p>
            <p className="text-3xl font-bold text-primary">{filteredMeetings.length}</p>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground mb-1">This Week</p>
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">
              {
                filteredMeetings.filter((m) => {
                  const meetingDate = new Date(m.date);
                  const now = new Date();
                  const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
                  const weekEnd = new Date(weekStart);
                  weekEnd.setDate(weekStart.getDate() + 6);
                  return meetingDate >= weekStart && meetingDate <= weekEnd;
                }).length
              }
            </p>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground mb-1">Action Items</p>
            <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
              {filteredMeetings.reduce(
                (acc, m) => acc + (m.actionItems?.filter((a) => !a.completed).length || 0),
                0
              )}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-border">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground mb-1">Clients</p>
            <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              {new Set(filteredMeetings.map((m) => m.clientId).filter(Boolean)).size}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Controls */}
      <Card className="border border-border mb-6">
        <CardContent className="p-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="search" className="mb-2 block">Search meetings</Label>
              <Input
                id="search"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by summary or participant..."
              />
            </div>

            <div>
              <Label htmlFor="client-filter" className="mb-2 block">Client</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {mockClients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="mb-2 block">View</Label>
              <div className="flex rounded-lg bg-muted p-1">
                <Button
                  variant={viewMode === 'grouped' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grouped')}
                  className={cn(viewMode !== 'grouped' && 'text-muted-foreground')}
                >
                  Grouped
                </Button>
                <Button
                  variant={viewMode === 'all' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('all')}
                  className={cn(viewMode !== 'all' && 'text-muted-foreground')}
                >
                  All
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Meetings Alert */}
      {upcomingMeetings.length > 0 && (
        <Card className="border border-border mb-6 border-l-4 border-l-primary">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-primary" />
              <h3 className="font-semibold text-foreground">Upcoming Meetings</h3>
            </div>
            <div className="space-y-2">
              {upcomingMeetings.slice(0, 3).map((meeting) => {
                const client = mockClients.find((c) => c.id === meeting.clientId);
                return (
                  <div key={meeting.id} className="flex items-center justify-between text-sm">
                    <span className="text-foreground">
                      {client?.name} - {formatDate(meeting.date)}
                    </span>
                    <Button variant="link" size="sm" className="p-0 h-auto" asChild>
                      <Link to={`/meetings/${meeting.id}`}>View →</Link>
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Meetings Content */}
      {viewMode === 'grouped' ? (
        <div className="space-y-6">
          {Object.entries(groupedMeetings).map(([clientId, { client, meetings, projects }]) => (
            <Card key={clientId} className="border border-border">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">{client.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {meetings.length} meeting{meetings.length !== 1 ? 's' : ''} • {projects.length}{' '}
                      project{projects.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <Button variant="link" size="sm" className="p-0 h-auto" asChild>
                    <Link to={`/clients/${client.id}`}>View Client →</Link>
                  </Button>
                </div>

                <div className="space-y-3">
                  {meetings
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .map((meeting) => {
                      const project = projects.find((p) => p.id === meeting.projectId);
                      const pendingActions =
                        meeting.actionItems?.filter((a) => !a.completed).length || 0;

                      return (
                        <Link
                          key={meeting.id}
                          to={`/meetings/${meeting.id}`}
                          className="block p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm text-muted-foreground">
                                  {formatDate(meeting.date)}
                                </span>
                                {project && (
                                  <>
                                    <span className="text-muted-foreground/50">•</span>
                                    <span className="text-sm text-primary">
                                      {project.name}
                                    </span>
                                  </>
                                )}
                                {pendingActions > 0 && (
                                  <>
                                    <span className="text-muted-foreground/50">•</span>
                                    <Badge variant="outline" className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-0">
                                      {pendingActions} action{pendingActions !== 1 ? 's' : ''}
                                    </Badge>
                                  </>
                                )}
                              </div>
                              <p className="text-foreground font-medium mb-1">
                                {meeting.summary || 'No summary available'}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {meeting.participants.length} participant
                                {meeting.participants.length !== 1 ? 's' : ''}:{' '}
                                {meeting.participants.join(', ')}
                              </p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-muted-foreground ml-2" />
                          </div>
                        </Link>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          ))}

          {Object.keys(groupedMeetings).length === 0 && (
            <Card className="border border-border">
              <CardContent className="p-6 text-center py-12">
                <Calendar className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No meetings found</h3>
                <p className="text-muted-foreground mb-4">
                  {selectedClient !== 'all' || searchTerm
                    ? 'Try adjusting your filters'
                    : 'Start documenting your client meetings'}
                </p>
                <Button asChild>
                  <Link to="/meetings/new">
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Meeting
                  </Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        <Card className="border border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">All Meetings</h3>
              <span className="text-sm text-muted-foreground">
                {filteredMeetings.length} meeting{filteredMeetings.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="space-y-3">
              {filteredMeetings
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                .map((meeting) => {
                  const client = mockClients.find((c) => c.id === meeting.clientId);
                  const project = mockProjects.find((p) => p.id === meeting.projectId);
                  const pendingActions = meeting.actionItems?.filter((a) => !a.completed).length || 0;

                  return (
                    <Link
                      key={meeting.id}
                      to={`/meetings/${meeting.id}`}
                      className="block p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm text-muted-foreground">{formatDate(meeting.date)}</span>
                            {client && (
                              <>
                                <span className="text-muted-foreground/50">•</span>
                                <span className="text-sm font-medium text-foreground">
                                  {client.name}
                                </span>
                              </>
                            )}
                            {project && (
                              <>
                                <span className="text-muted-foreground/50">•</span>
                                <span className="text-sm text-primary">{project.name}</span>
                              </>
                            )}
                            {pendingActions > 0 && (
                              <>
                                <span className="text-muted-foreground/50">•</span>
                                <Badge variant="outline" className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-0">
                                  {pendingActions} action{pendingActions !== 1 ? 's' : ''}
                                </Badge>
                              </>
                            )}
                          </div>
                          <p className="text-foreground font-medium mb-1">
                            {meeting.summary || 'No summary available'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {meeting.participants.length} participant
                            {meeting.participants.length !== 1 ? 's' : ''}:{' '}
                            {meeting.participants.join(', ')}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground ml-2" />
                      </div>
                    </Link>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}
    </AppLayout>
  );
}

export default function MeetingsPage() {
  return (
    <ProtectedRoute>
      <MeetingsContent />
    </ProtectedRoute>
  );
}
